import { useId } from "react";

import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import { FormControl, FormHelperText, FormLabel, Grid, Stack, Typography } from "@mui/material";
import { FormDialog, FormikTextField, isValidDate } from "@shared";

import { AppointmentExchangeResponse } from "@/api";
import { FormikCheckbox } from "@/common/components/fields/formik/formik-checkbox/FormikCheckbox";
import { FormikDatePicker } from "@/common/components/fields/formik/formik-date-picker/FormikDatePicker";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { useAppointmentLabels } from "@/common/hooks/useAppointmentLabels";
import { exchangeSectionLabelSx } from "@/pages/exchange/Exchange.styles";
import { formatExchangeAppointment } from "@/pages/exchange/Exchange.utils";
import { joinRequestedPurposeLabels } from "@/pages/exchange/components/exchange-table/ExchangeTable.utils";
import { ExchangeableAppointmentPicker } from "@/pages/exchange/components/exchangeable-appointment-picker/ExchangeableAppointmentPicker";
import { Trans, useTranslation } from "@/translations";

import { useMakeOffer } from "./hooks/useMakeOffer";

interface Props {
  exchange: AppointmentExchangeResponse;
  onClose: () => void;
}

/** "Angebot abgeben": pick one own Termin as counter-offer on a colleague's Tauschanfrage. */
export function MakeOfferDialog({ exchange, onClose }: Props) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const labels = useAppointmentLabels();
  const ownServiceLabelId = useId();
  const ownServiceErrorId = useId();

  const {
    formik,
    counselorLabel,
    appointments,
    isLoading,
    isError,
    datesSelected,
    rangeValid,
    toggleAppointment,
    clearAppointment,
    handleFromChange,
    handleFilterChange,
    submitDisabled,
    appointmentError,
  } = useMakeOffer({ exchange, onClose });

  const { from, onlyRequestedPurposes } = formik.values;

  return (
    <FormDialog
      title={t("exchange.offerDialog.title")}
      onClose={onClose}
      formik={formik}
      submitLabel={t("exchange.offerDialog.submit")}
      submitStartIcon={<SwapHorizRoundedIcon />}
      submitDisabled={submitDisabled}
      fullWidth
    >
      <Stack spacing={3}>
        <Typography color="text.secondary">
          <Trans
            i18nKey="exchange.offerDialog.summary"
            values={{
              counselor: counselorLabel,
              appointment: formatExchangeAppointment(exchange, locales[language], t),
              purpose: labels.purposeLabelOf(exchange.purposeKey),
              requested: joinRequestedPurposeLabels(exchange.requestedPurposeKeys, labels, t),
            }}
            components={{ bold: <strong /> }}
          />
        </Typography>

        <FormControl component="fieldset" fullWidth error={appointmentError !== undefined}>
          <FormLabel component="legend" required id={ownServiceLabelId} sx={exchangeSectionLabelSx}>
            {t("exchange.offerDialog.ownServiceSection")}
          </FormLabel>
          {/*
            Top-aligned, not centred: FormikDatePicker always reserves a helper-text row below the
            input, so centring lines the checkbox up with the middle of input-plus-empty-row and
            leaves it sitting visibly lower than the fields. The input box (40px) and the checkbox
            (42px) are the same height, so sharing a top edge is what puts them on one line.
          */}
          <Grid container columnSpacing={2} alignItems="flex-start">
            <Grid size={3}>
              <FormikDatePicker
                formik={formik}
                fieldName="from"
                label={t("exchange.offerDialog.from")}
                required
                onValueChange={handleFromChange}
              />
            </Grid>
            <Grid size={3}>
              <FormikDatePicker
                formik={formik}
                fieldName="till"
                label={t("exchange.offerDialog.till")}
                required
                minDate={isValidDate(from) ? from : undefined}
                onValueChange={clearAppointment}
              />
            </Grid>
            <Grid size={6}>
              <FormikCheckbox
                formik={formik}
                fieldName="onlyRequestedPurposes"
                label={t("exchange.offerDialog.onlyRequestedPurposes")}
                onValueChange={handleFilterChange}
              />
            </Grid>
          </Grid>
          <ExchangeableAppointmentPicker
            labelledById={ownServiceLabelId}
            datesSelected={datesSelected}
            rangeValid={rangeValid}
            appointments={appointments}
            isLoading={isLoading}
            isError={isError}
            selectedAppointmentId={formik.values.appointmentId}
            onToggle={toggleAppointment}
            hasError={appointmentError !== undefined}
            describedById={ownServiceErrorId}
            emptyText={onlyRequestedPurposes ? t("exchange.offerDialog.picker.emptyFiltered") : undefined}
          />
          {appointmentError !== undefined && <FormHelperText id={ownServiceErrorId}>{appointmentError}</FormHelperText>}
        </FormControl>

        <FormikTextField
          formik={formik}
          fieldName="note"
          label={t("exchange.offerDialog.note")}
          multiline
          rows={3}
          placeholder={t("exchange.offerDialog.notePlaceholder")}
        />
      </Stack>
    </FormDialog>
  );
}
